import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Dialog, TextInput, Checkbox } from 'uxd-design-system-components';
import isEmail from 'validator/lib/isEmail';
import getText from '../../assets/locale/locale';
import { LocaleContext } from '../../contexts';
import './WebProspectFormModal.scss';

const WebProspectFormModal = ({
  displayWebProspectFormModal,
  setDisplayWebProspectFormModal,
  webProspectData,
  setWebProspectData,
  setConsentDateTimestamp,
  saveResults,
}) => {
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isConsentCheckboxSelected, setIsConsentCheckboxSelected] =
    useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const locId = useContext(LocaleContext);
  // this regex will match strings that contain letters, hyphens, and apostrophies
  // for example, "Edward-Jones" is valid input, as is "O'Neill"
  const nameRegex = /^[a-zA-Z ‘’´'-]*$/;
  // this regex will match strings that contain all white spaces (space, tab, etc.)
  const whiteSpaceRegex = /^\s*$/;

  const invalidFirstNameError =
    getText(locId, 'Results', 'firstNameError', 'WebProspectModalForm') +
    getText(locId, 'Results', 'validNameChars', 'WebProspectModalForm');

  const invalidLastNameError =
    getText(locId, 'Results', 'lastNameError', 'WebProspectModalForm') +
    getText(locId, 'Results', 'validNameChars', 'WebProspectModalForm');

  const isValidFirstName = () => {
    if (
      firstName.length === 0 ||
      firstName.match(whiteSpaceRegex) ||
      !firstName.match(nameRegex)
    ) {
      setFirstNameError(invalidFirstNameError);
      return false;
    }
    setFirstName(firstName);
    setFirstNameError(null);
    setWebProspectData({
      ...webProspectData,
      firstName: firstName.trim(),
    });
    return true;
  };
  const isValidLastName = () => {
    if (
      lastName.length === 0 ||
      lastName.match(whiteSpaceRegex) ||
      !lastName.match(nameRegex)
    ) {
      setLastNameError(invalidLastNameError);
      return false;
    }
    setLastName(lastName);
    setLastNameError(null);
    setWebProspectData({
      ...webProspectData,
      lastName: lastName.trim(),
    });
    return true;
  };
  const isValidEmail = () => {
    if (email.length === 0 || !isEmail(email)) {
      setEmailError(
        getText(locId, 'Results', 'emailError', 'WebProspectModalForm')
      );
      return false;
    }
    setEmail(email);
    setEmailError(null);
    setWebProspectData({
      ...webProspectData,
      email: email.trim(),
    });
    return true;
  };

  const isConsentChecked = () => {
    if (!isConsentCheckboxSelected) {
      setShowConsentError(true);
      return false;
    }
    setShowConsentError(false);
    return true;
  };

  const saveButtonHandler = () => {
    let isValidForm = true;
    if (!isValidFirstName()) isValidForm = false;
    if (!isValidLastName()) isValidForm = false;
    if (!isValidEmail()) isValidForm = false;
    if (!isConsentChecked()) isValidForm = false;
    if (isValidForm) {
      setDisplayWebProspectFormModal(false);
      saveResults();
    }
  };

  const cancelButtonHandler = () => {
    setDisplayWebProspectFormModal(false);
  };
  const headerTextHandler = () =>
    getText(locId, 'Results', 'header', 'WebProspectModalForm');

  const consentCheckboxHandler = () => {
    let consentDateTimestamp;
    if (!isConsentCheckboxSelected) {
      consentDateTimestamp = new Date().toISOString();
      setIsConsentCheckboxSelected(true);
      setShowConsentError(false);
    } else {
      consentDateTimestamp = null;
      setIsConsentCheckboxSelected(false);
    }
    setConsentDateTimestamp(consentDateTimestamp);
  };

  const buttons = [
    {
      text: getText(locId, 'Results', 'cancelButton', 'WebProspectModalForm'),
      onClick: cancelButtonHandler,
      styleVariant: 'secondary',
    },
    {
      text: getText(locId, 'Results', 'shareButton', 'WebProspectModalForm'),
      onClick: saveButtonHandler,
      styleVariant: 'primary',
    },
  ];

  return (
    <>
      <Dialog
        title=""
        open={displayWebProspectFormModal}
        buttons={buttons}
        className="webProspectFormModal"
        onRequestClose={() => setDisplayWebProspectFormModal(false)}
      >
        <div className="title-container">
          <h3>{headerTextHandler()}</h3>
        </div>
        <p
          className="web-prospect-form-modal-paragraph"
          data-testid="web-prospect-form-modal-paragraph"
        >
          {getText(locId, 'Results', 'paragraphText', 'WebProspectModalForm')}
        </p>
        <form onSubmit={saveButtonHandler}>
          <TextInput
            id="first-name"
            label={getText(
              locId,
              'Results',
              'firstNameLabel',
              'WebProspectModalForm'
            )}
            name="first-name"
            value={firstName}
            type="text"
            onChange={(text) => {
              setFirstName(text);
              setWebProspectData({
                ...webProspectData,
                firstName: text.trim(),
              });
            }}
            onBlur={isValidFirstName}
            error={firstNameError}
          />
          <TextInput
            id="last-name"
            label={getText(
              locId,
              'Results',
              'lastNameLabel',
              'WebProspectModalForm'
            )}
            name="last-name"
            value={lastName}
            type="text"
            onChange={(text) => {
              setLastName(text);
              setWebProspectData({
                ...webProspectData,
                lastName: text.trim(),
              });
            }}
            onBlur={isValidLastName}
            error={lastNameError}
          />
          <TextInput
            id="email"
            label={getText(
              locId,
              'Results',
              'emailLabel',
              'WebProspectModalForm'
            )}
            name="email"
            value={email}
            type="email"
            onChange={(text) => {
              setEmail(text);
              setWebProspectData({
                ...webProspectData,
                email: text.trim(),
              });
            }}
            onBlur={isValidEmail}
            error={emailError}
          />
          <div className={showConsentError ? 'consent-checkbox-error' : ''}>
            <Checkbox
              checked={isConsentCheckboxSelected}
              id="consent"
              name="consent"
              label={getText(
                locId,
                'Results',
                'consentLabel',
                'WebProspectModalForm'
              )}
              onChange={() => {
                consentCheckboxHandler();
              }}
            />
          </div>
          {showConsentError ? (
            <p className="consent-error">
              {getText(
                locId,
                'Results',
                'consentError',
                'WebProspectModalForm'
              )}
            </p>
          ) : null}
        </form>
      </Dialog>
    </>
  );
};

WebProspectFormModal.propTypes = {
  displayWebProspectFormModal: PropTypes.bool.isRequired,
  setDisplayWebProspectFormModal: PropTypes.func.isRequired,
  saveResults: PropTypes.func.isRequired,
  webProspectData: PropTypes.objectOf(PropTypes.string),
  setWebProspectData: PropTypes.func.isRequired,
  setConsentDateTimestamp: PropTypes.func.isRequired,
};
WebProspectFormModal.defaultProps = {
  webProspectData: {},
};

export default WebProspectFormModal;
